/**
 * CR456: Tab 1 — Adapters CRUD + Scaffold Generator
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, RefreshCw, Copy, Download } from "lucide-react";
import { useAdapters, type ImageCaptureAdapter } from "./useAdapters";
import { AdapterFormModal } from "./AdapterFormModal";
import { useToast } from "@/hooks/use-toast";

function generateScaffold(adapter: ImageCaptureAdapter): string {
  const required = Array.isArray(adapter.required_context) ? adapter.required_context : [];
  const pyAccess = (p: string) =>
    "ctx" + p.split(".").map((seg) => `["${seg}"]`).join("");
  const ctxDocs = required.map((p) => `    ${pyAccess(p)}: required`).join("\n");
  const paramDocs = adapter.param_schema
    ? `\n    ctx["adapter_params"]: validated by param_schema`
    : "";

  return `# adapters/${adapter.adapter_key}.py
# Auto-generated scaffold — CR456 Image Capture Studio
# Version: ${adapter.version ?? "1.0.0"}
# Entrypoint: ${adapter.entrypoint}

from typing import Dict, Any, List


async def run(ctx: Dict[str, Any]) -> Dict[str, Any]:
    """
    Image Capture Adapter: ${adapter.display_name}

    Required context:
${ctxDocs || "    (none)"}${paramDocs}

    Must return:
        {
            "pages": [
                {
                    "source_page_number": int,
                    "image_bytes": bytes,
                    "width": int,
                    "height": int,
                }
            ],
            "diagnostics": {
                "ok": bool,
                ...
            }
        }
    """
    source_url: str | None = ctx.get("urls", {}).get("source_url")
    postal_code: str | None = ctx.get("region", {}).get("postal_code")
    max_pages: int = ctx.get("limits", {}).get("max_pages") or 10
    adapter_params: dict = ctx.get("adapter_params") or {}

    # TODO: implement adapter logic here
    pages: List[Dict[str, Any]] = []

    return {
        "pages": pages,
        "diagnostics": {
            "ok": len(pages) > 0,
            "source_url": source_url,
            "postal_code": postal_code,
            "pages_captured": len(pages),
        },
    }
`;
}

export function AdaptersTab() {
  const { adapters, loading, fetchAdapters, createAdapter, updateAdapter, deleteAdapter } = useAdapters();
  const { toast } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [editAdapter, setEditAdapter] = useState<ImageCaptureAdapter | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ImageCaptureAdapter | null>(null);

  const handleSave = async (payload: Partial<ImageCaptureAdapter>) => {
    if (editAdapter) {
      return updateAdapter(editAdapter.adapter_key, payload);
    }
    return createAdapter(payload);
  };

  const handleScaffold = (adapter: ImageCaptureAdapter, mode: "copy" | "download") => {
    const code = generateScaffold(adapter);
    if (mode === "copy") {
      navigator.clipboard.writeText(code);
      toast({ title: "Scaffold copied to clipboard" });
    } else {
      const blob = new Blob([code], { type: "text/x-python" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${adapter.adapter_key}.py`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: `Downloaded ${adapter.adapter_key}.py` });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Registry of image capture adapters. Each adapter maps to a Python callable deployed with the runtime.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAdapters} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => { setEditAdapter(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4 mr-1" />
            Register Adapter
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Key</TableHead>
              <TableHead>Display Name</TableHead>
              <TableHead>Entrypoint</TableHead>
              <TableHead>Version</TableHead>
              <TableHead className="w-[80px]">Enabled</TableHead>
              <TableHead>Timeout</TableHead>
              <TableHead>Context</TableHead>
              <TableHead className="w-[160px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</TableCell>
              </TableRow>
            ) : adapters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No adapters registered yet. Click "Register Adapter" to add one.
                </TableCell>
              </TableRow>
            ) : (
              adapters.map((a) => (
                <TableRow key={a.adapter_key}>
                  <TableCell className="font-mono text-sm font-medium">{a.adapter_key}</TableCell>
                  <TableCell>{a.display_name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{a.entrypoint}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{a.version}</Badge>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={a.enabled}
                      onCheckedChange={(v) => updateAdapter(a.adapter_key, { enabled: v })}
                    />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {a.default_timeout_ms ? `${a.default_timeout_ms.toLocaleString()}ms` : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(a.required_context) && a.required_context.length > 0
                        ? a.required_context.map((c) => (
                            <Badge key={c} variant="secondary" className="font-mono text-[10px] px-1">
                              {c}
                            </Badge>
                          ))
                        : <span className="text-xs text-muted-foreground">—</span>
                      }
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditAdapter(a); setFormOpen(true); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Copy scaffold" onClick={() => handleScaffold(a, "copy")}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Download scaffold" onClick={() => handleScaffold(a, "download")}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteConfirm(a)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AdapterFormModal
        open={formOpen}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditAdapter(null); }}
        adapter={editAdapter}
        onSave={handleSave}
      />

      <AlertDialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Adapter "{deleteConfirm?.adapter_key}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the adapter from the registry. Sources mapped to it will lose their mapping.
              The Python file in the repo is not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (deleteConfirm) {
                  await deleteAdapter(deleteConfirm.adapter_key);
                  setDeleteConfirm(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
